import React, { useEffect, useState } from "react";
import { ClipboardCheck, Loader2, UserCheck } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { StatusPill, money } from "@/components/finance/shared";
import { LIQ_SECTIONS, SectionLines, liqTotal, lineLabel, buildLines } from "@/components/finance/LiquidationTab";

const ALL = LIQ_SECTIONS.flatMap(([, cats]) => cats);
const blank = () => Object.fromEntries(ALL.map((c) => [c, ""]));
const LIQUIDATORS = ["admin", "broker_manager", "finance_staff", "finance_manager"];

function LiquidationView({ lq }) {
  return (
    <div data-testid={`liq-view-${lq.number}`} className="mt-3 space-y-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <span className="inline-flex items-center gap-1.5 text-emerald-200"><UserCheck size={14} /> Liquidated by <b data-testid={`liq-by-${lq.number}`}>{lq.created_by_name}</b></span>
        <span className="text-xs text-slate-500">{new Date(lq.created_at).toLocaleString()}</span>
        <StatusPill status={lq.status} />
        {lq.decision?.by_name && <span className="text-xs text-slate-400">Reviewed by {lq.decision.by_name}{lq.decision.remarks ? `: ${lq.decision.remarks}` : ""}</span>}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {LIQ_SECTIONS.map(([title]) => {
          const lines = (lq.items || []).filter((it) => it.section === title);
          return (
            <div key={title} className="bg-slate-950/50 border border-slate-800 rounded-md p-3">
              <div className="flex justify-between font-display font-bold text-sm mb-1.5"><span>{title}</span>
                <span className="font-mono-ops">{money(title === "Receipted" ? lq.receipted_total : lq.unreceipted_total)}</span></div>
              {lines.length === 0 ? <div className="text-xs text-slate-500">None</div> : lines.map((it, i) => (
                <div key={i} className="flex justify-between text-xs text-slate-300 py-0.5">
                  <span>{lineLabel(it)}</span><span className="font-mono-ops">{money(it.amount)}</span>
                </div>
              ))}
            </div>
          );
        })}
      </div>
      <div className="text-right font-mono-ops text-sm text-slate-400">
        Spent <span className="text-slate-100">{money(lq.total)}</span> of {money(lq.fund_amount)} · Balance{" "}
        <span className={lq.variance < 0 ? "text-rose-300" : "text-emerald-300"}>{money(lq.variance)}</span>
      </div>
      {lq.notes && <div className="text-xs text-slate-400">Notes: {lq.notes}</div>}
    </div>
  );
}

function LiquidationEntry({ sh, fund, onSaved }) {
  const [amts, setAmts] = useState(blank());
  const [specify, setSpecify] = useState("");
  const [notes, setNotes] = useState("");
  const [extras, setExtras] = useState([]);
  const [busy, setBusy] = useState(false);
  const total = liqTotal(amts, extras);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/shipments/${sh.id}/liquidation`, {
        fund_request_id: fund.id, notes, lines: buildLines(amts, specify, extras),
      });
      toast.success(`${data.number} submitted to Finance for review`);
      onSaved();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid={`liq-entry-${fund.number}`} className="mt-3 space-y-3">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {LIQ_SECTIONS.map(([title, cats]) => (
          <SectionLines key={title} title={title} cats={cats} amts={amts} setAmts={setAmts} specify={specify} setSpecify={setSpecify} extras={extras} setExtras={setExtras} />
        ))}
      </div>
      <Input data-testid="input-shipment-liq-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" className="bg-slate-950 border-slate-700" />
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="font-mono-ops text-sm text-slate-400">Spent <span data-testid="shipment-liq-total" className="text-slate-100">{money(total)}</span> of {money(fund.amount)} · Balance{" "}
          <span className={fund.amount - total < 0 ? "text-rose-300" : "text-emerald-300"}>{money(fund.amount - total)}</span></div>
        <Button data-testid="button-submit-shipment-liquidation" onClick={save} disabled={busy || total <= 0} className="bg-blue-600 hover:bg-blue-500">
          {busy && <Loader2 size={14} className="mr-2 animate-spin" />} Submit liquidation
        </Button>
      </div>
    </div>
  );
}

export function LiquidationPanel({ sh }) {
  const { user } = useAuth();
  const [funds, setFunds] = useState(null);
  const load = () => api.get(`/shipments/${sh.id}/cash-advances`).then((r) => setFunds(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sh.id]);
  const canLiquidate = (f) => LIQUIDATORS.includes(user?.role) || f.created_by_id === user?.id;
  const approved = (funds || []).filter((f) => f.status === "Approved");

  return (
    <div data-testid="liquidation-panel" className="glass-card rounded-lg p-6">
      <div className="flex items-center gap-2 font-display text-xl font-bold mb-1"><ClipboardCheck size={18} className="text-sky-400" /> Liquidation</div>
      <p className="text-xs text-slate-500 mb-4">Actual spending against each approved cash advance for this shipment, in the Receipted / Unreceipted format. Submitted liquidations go to Finance → Liquidation for review.</p>
      {funds === null ? <div className="text-sm text-slate-500">Loading…</div> : approved.length === 0 ? (
        <div data-testid="liq-none" className="text-sm text-slate-500">No approved cash advance for this shipment yet, so there is nothing to liquidate.</div>
      ) : approved.map((f) => (
        <div key={f.id} data-testid={`liq-fund-${f.number}`} className="border border-slate-800 rounded-md p-4 mb-3 bg-slate-950/30">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm"><span className="font-mono-ops text-slate-200">{f.number}</span>
              <span className="text-slate-500 text-xs"> · requested by {f.created_by_name}</span></div>
            <span className="font-mono-ops text-sky-300">{money(f.amount)}</span>
          </div>
          {f.liquidation ? <LiquidationView lq={f.liquidation} />
            : canLiquidate(f) ? <LiquidationEntry sh={sh} fund={f} onSaved={load} />
            : <div className="mt-2 text-xs text-amber-300">Awaiting liquidation by {f.created_by_name}.</div>}
        </div>
      ))}
    </div>
  );
}
